import React, { useState, useRef, useEffect } from 'react';
import {
  Button, Input, Row, Col, UncontrolledTooltip,
  ButtonDropdown, DropdownToggle, DropdownMenu, Label, Form
} from "reactstrap";
import EmojiPicker from 'emoji-picker-react';
import { uploadAttachment } from '../../../../services/attachments';
import { Progress } from 'antd';
import Dropzone from '../DropZone';
import { useSocket } from '../../../../store/context/SocketProvider';

function ChatInput(props) {
  const [textMessage, settextMessage] = useState(props.defaultValue || "");
  const [isOpen, setisOpen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [file, setfile] = useState([]);
  const [status, setStatus] = useState("active");
  const [fileImage, setfileImage] = useState([]);
  const containerRef = useRef(null);
  const [typing, setTyping] = useState(false);
  const [isSomeoneTyping, setIsSomeoneTyping] = useState(false);
  const [whoIsTyping, setWhoIsTyping] = useState("");
  const typingTimeout = useRef(null);

  // ── Reply state ──
  const [replyTo, setReplyTo] = useState(null); // the chat object being replied to

  const { socket } = useSocket();

  // Expose setReplyTo so parent can call it via ref or prop
  useEffect(() => {
    if (props.replyTarget) {
      setReplyTo(props.replyTarget);
      containerRef.current?.focus();
    }
  }, [props.replyTarget]);

  const cancelReply = () => {
    setReplyTo(null);
    props.setReplyTarget(null);
  };

  useEffect(() => {
    const handleTypingStart = ({ conversationId, senderId }) => {
      if (
        conversationId === props.conversationId &&
        senderId?._id !== props.currentUser?._id
      ) {
        setWhoIsTyping(`${senderId?.contactName?.first} ${senderId?.contactName?.last}`);
        setIsSomeoneTyping(true);
      }
    };
    const handleTypingStop = ({ conversationId, senderId }) => {
      if (
        conversationId === props.conversationId &&
        senderId?._id !== props.currentUser?._id
      ) {
        setWhoIsTyping("");
        setIsSomeoneTyping(false);
      }
    };
    socket?.on("typing-start", handleTypingStart);
    socket?.on("typing-stop", handleTypingStop);
    return () => {
      socket?.off("typing-start", handleTypingStart);
      socket?.off("typing-stop", handleTypingStop);
    };
  }, [socket, props.conversationId, props.currentUser]);

  useEffect(() => {
    window.addEventListener('paste', (e) => {
      handleFileChange({ target: { files: e.clipboardData.files } });
    });
    return () => window.removeEventListener("paste", () => {});
  }, []);

  useEffect(() => {
    if (props.defaultValue) settextMessage(props.defaultValue);
  }, [props.defaultValue]);

  const toggleOpen = () => setisOpen(!isOpen);

  const handleChange = (e) => {
    settextMessage(e.target.value);
    if (!typing) {
      setTyping(true);
      socket?.emit("typing-start", {
        conversationId: props.conversationId,
        senderId: props.currentUser,
        type: props.isGroupchat ? "group" : "individual",
      });
    }
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socket?.emit("typing-stop", {
        conversationId: props.conversationId,
        senderId: props.currentUser,
        type: props.isGroupchat ? "group" : "individual",
      });
      setTyping(false);
    }, 1500);
  };

  const onEmojiClick = (event) => {
    settextMessage(textMessage + event?.emoji);
  };

  const handleFileChange = async (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      setfile([{ fileName: files[0].name, fileSize: files[0].size }]);
      let data = new FormData();
      for (let item of files) data.append("attachments", item);
      try {
        const res = await uploadAttachment(data, {
          headers: {
            'Content-Type': files[0].type,
            'Content-Length': files[0].size,
          },
          onUploadProgress: (progressEvent) => {
            const prog = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setProgress(prog);
          },
        });
        if (res.status === 200) {
          setfile([{
            fileName: files[0].name,
            fileSize: files[0].size,
            filePath: res.data[0].filePath,
            fileType: res.data[0].fileType,
          }]);
        }
      } catch (e) {
        setStatus("exception");
      }
    }
  };

  const convertToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => resolve(fileReader.result);
      fileReader.onerror = (error) => reject(error);
    });

  const handleImageChange = async (e) => {
    const files = e.target.files;
    if (files.length > 3) {
      alert("You cannot upload more than 3 images at one time");
      return;
    }
    let media = [];
    for (let key in files) {
      if (key < files.length) {
        const uri = await convertToBase64(files[key]);
        media.push({ file: files[key], uri });
      }
    }

    console.log("Selected images: ", media);
    setfileImage(media);
  };

  const handleDrop = async (files) => {
    props?.setDrag(false);
    await handleFileChange({ target: { files } });
  };

  const handleRemoveImage = (key) => {
    setfileImage((prev) => prev.filter((_, index) => index !== key));
  };

  const onaddMessage = async (e, textMessage) => {
    e.preventDefault();

    if (props.isEditing && textMessage !== "") {
      props.updateMessages(textMessage);
      settextMessage("");
      return;
    }

    if (textMessage !== "") {
      // pass replyTo along with the message
      props.onaddMessage(textMessage, "textMessage", replyTo?._id || null);
      settextMessage("");
      setReplyTo(null);
      return;
    }

    if (file.length > 0) {
      props.onaddMessage(file, "imageMessage", replyTo?._id || null);
      setfile([]);
      setReplyTo(null);
      return;
    }

    if (fileImage.length > 0) {
      let data = new FormData();
      for (let item of fileImage) data.append("attachments", item.file);
      try {
        const res = await uploadAttachment(data);
        if (res.status === 200) {
          props.onaddMessage(res.data, "imageMessage", replyTo?._id || null);
          setfileImage([]);
          setReplyTo(null);
        }
      } catch (e) {
        console.log("error", e.message);
      }
    }
  };

  const showUploadedImages = (data) => {
    const fileImageData = data ? data : fileImage;
    return fileImageData.map((file, key) => (
      <div style={{ padding: 5 }} key={key}>
        <img
          src={data ? `${process.env.REACT_APP_API_URL}/${file.filePath}` : file.uri}
          height="50px"
          width="60px"
          alt=""
        />
        <span
          onClick={() => handleRemoveImage(key)}
          style={{
            display: 'block', height: 20, width: 20, borderRadius: 10,
            backgroundColor: 'white', color: 'white', position: 'relative',
            top: -65, left: -5, textAlign: 'center', border: 'red 1px solid',
          }}
          className="cursor-pointer"
        >❌</span>
      </div>
    ));
  };

  const showUploadedFiles = () =>
    file.map((file, key) => {
      if (["image/jpeg", "image/png"].includes(file.fileType)) {
        return showUploadedImages([{ ...file }]);
      }
      return (
        <div style={{ padding: 5, minWidth: 150 }} key={key}>
          <div className="mt-4">
            <span className="text-md text-gray-600">{file.fileName}</span>
            <Progress percent={progress} size="small" status={progress === 100 ? "" : status} />
          </div>
          <span
            onClick={() => { setProgress(0); setfile([]); }}
            style={{
              display: 'block', height: 20, width: 20, borderRadius: 10,
              backgroundColor: 'white', color: 'white', position: 'relative',
              top: -65, left: -5, textAlign: 'center', border: 'red 1px solid',
            }}
            className="cursor-pointer"
          >❌</span>
        </div>
      );
    });

  console.log("Replying to:", replyTo);

  // ── Reply preview helpers ──
  const replyIsImage = replyTo?.media?.[0]?.fileType?.startsWith("image");
  const replyIsAudio = replyTo?.media?.[0]?.fileType === "audio/webm";
  const replyIsFile  = replyTo?.media?.[0] && !replyIsImage && !replyIsAudio;
  // const replySenderName =
  //   replyTo?.sender === props.currentUser?._id ? "You" : replyTo?.senderName || "Unknown";

  return (
    <React.Fragment>

      {/* ── Typing indicator ── */}
      {isSomeoneTyping && (
        <div className="mb-0 ps-2 d-flex align-items-center text-muted">
          <span>{whoIsTyping} Typing</span>
          <span className="d-flex ms-2">
            <span className="dot bg-secondary rounded-circle me-1"></span>
            <span className="dot bg-secondary rounded-circle me-1"></span>
            <span className="dot bg-secondary rounded-circle"></span>
          </span>
        </div>
      )}

      {/* ── Reply preview bar ── */}
      {replyTo && (
        <div className="reply-preview-bar">
          <div className="reply-preview-indicator">
            <div className="reply-preview-label">
              Replying to message
            </div>

            {replyTo.message && (
              <div className="reply-preview-text">{replyTo.message}</div>
            )}

            {replyIsAudio && (
              <div className="reply-preview-media">
                <i className="ri-mic-fill" style={{ fontSize: 13 }} />
                Voice message
              </div>
            )}

            {replyIsFile && (
              <div className="reply-preview-media">
                <i className="ri-file-line" style={{ fontSize: 13 }} />
                {replyTo.media[0].fileName}
              </div>
            )}
          </div>

          {replyIsImage && (
            <img
              src={`${process.env.REACT_APP_API_URL}/${replyTo.media[0].filePath}`}
              alt="reply preview"
              className="reply-preview-img"
            />
          )}

          <button
            className="reply-preview-close"
            onClick={cancelReply}
            aria-label="Cancel reply"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* ── Input area ── */}
      <div className="p-2 border-top mb-0 w-100">
        <div
          className="flex justify-start item-center"
          style={props?.isForVideoChat ? {} : { padding: 5 }}
        >
          {showUploadedImages()}
          {showUploadedFiles()}
          {props?.isDrag && <Dropzone handleDrop={handleDrop} />}
        </div>

        <Form onSubmit={(e) => onaddMessage(e, textMessage)}>
          <Row className="g-0">
            <Col>
              <div>
                <Input
                  ref={containerRef}
                  type="text"
                  value={textMessage}
                  onChange={handleChange}
                  className="form-control form-control-lg bg-light border-light"
                  placeholder={replyTo ? `Reply to message...` : "Enter Message..."}
                  onFocus={props.msgSeenUpdate}
                />
              </div>
            </Col>
            <Col xs="auto">
              <div className="chat-input-links ms-md-2">
                <ul className="list-inline mb-0 ms-0">

                  {/* Emoji */}
                  <li className="list-inline-item">
                    <ButtonDropdown
                      className="emoji-dropdown"
                      direction="up"
                      isOpen={isOpen}
                      toggle={toggleOpen}
                    >
                      <DropdownToggle
                        id="emoji"
                        color="link"
                        className="text-decoration-none font-size-16 btn-lg waves-effect"
                      >
                        <i className="ri-emotion-happy-line"></i>
                      </DropdownToggle>
                      <DropdownMenu className="dropdown-menu-end">
                        <EmojiPicker onEmojiClick={onEmojiClick} />
                      </DropdownMenu>
                    </ButtonDropdown>
                    <UncontrolledTooltip target="emoji" placement="top">
                      Emoji
                    </UncontrolledTooltip>
                  </li>

                  {/* File attachment */}
                  <li className="list-inline-item input-file">
                    <Label
                      id="files"
                      className="btn btn-link text-decoration-none font-size-16 btn-lg waves-effect"
                    >
                      <i className="ri-attachment-line"></i>
                      <Input
                        onChange={(e) => handleFileChange(e)}
                        type="file"
                        name="fileInput"
                        size="60"
                        accept=".txt,.xlx,.xlsx,.doc,.docx,.pdf,.ppt,.pptx,.zip"
                      />
                    </Label>
                    <UncontrolledTooltip target="files" placement="top">
                      Attached File
                    </UncontrolledTooltip>
                  </li>

                  {/* Mic / Stop recording */}
                  {!props?.isRecording ? (
                    <li
                      className="list-inline-item input-file"
                      onClick={props?.startRecording}
                    >
                      <Label
                        id="mic"
                        className="btn btn-link text-decoration-none font-size-16 btn-lg waves-effect"
                      >
                        <i className="ri-mic-fill"></i>
                      </Label>
                      <UncontrolledTooltip target="mic" placement="top">
                        Start Recording
                      </UncontrolledTooltip>
                    </li>
                  ) : (
                    <li
                      className="list-inline-item input-file"
                      onClick={props?.stopRecording}
                    >
                      <Label
                        id="stop"
                        className="btn btn-link text-decoration-none font-size-16 btn-lg waves-effect"
                      >
                        <i className="ri-stop-circle-line"></i>
                      </Label>
                      <UncontrolledTooltip target="stop" placement="top">
                        Stop Recording
                      </UncontrolledTooltip>
                    </li>
                  )}

                  {/* Image upload */}
                  <li className="list-inline-item input-file">
                    <Label
                      id="images"
                      className="me-1 btn btn-link text-decoration-none font-size-16 btn-lg waves-effect"
                    >
                      <i className="ri-image-fill"></i>
                      <Input
                        onChange={(e) => handleImageChange(e)}
                        accept="image/*"
                        type="file"
                        name="fileInput"
                        size="60"
                        multiple
                      />
                    </Label>
                    <UncontrolledTooltip target="images" placement="top">
                      Images
                    </UncontrolledTooltip>
                  </li>

                  {/* Send */}
                  <li className="list-inline-item">
                    <Button
                      type="submit"
                      color="primary"
                      className="font-size-16 btn-lg chat-send waves-effect waves-light"
                    >
                      <i className="ri-send-plane-2-fill"></i>
                    </Button>
                  </li>

                </ul>
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </React.Fragment>
  );
}

export default ChatInput;